import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/homeData';
import { allVideos, getVideosByCategory } from '@/mocks/categoryData';

type SortKey = 'latest' | 'popular' | 'rating';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState<SortKey>('latest');
  const [activeCat, setActiveCat] = useState(slug || 'all');

  const currentCategory = categories.find((c) => c.slug === activeCat);
  const videos = useMemo(() => getVideosByCategory(activeCat), [activeCat]);

  const sortedVideos = useMemo(() => {
    const list = [...videos];
    if (sortBy === 'latest') list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    if (sortBy === 'popular') list.sort((a, b) => parseInt(b.views) - parseInt(a.views));
    if (sortBy === 'rating') list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [videos, sortBy]);

  const sortOptions: { key: SortKey; label: string }[] = [
    { key: 'latest', label: t('category.sort.latest') },
    { key: 'popular', label: t('category.sort.popular') },
    { key: 'rating', label: t('category.sort.rating') },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 pb-16">
        <div className="w-full px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">{t('breadcrumb.home')}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-400">{t('category.title')}</span>
            {currentCategory && activeCat !== 'all' && (
              <>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-700 font-medium">{currentCategory.name}</span>
              </>
            )}
          </nav>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">{t('category.title')}</h1>
            <p className="text-sm text-foreground-600">{t('category.subtitle')}</p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <button
              onClick={() => setActiveCat('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeCat === 'all'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
              }`}
            >
              {t('category.all')}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setActiveCat(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeCat === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2 mb-6">
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                  sortBy === opt.key
                    ? 'bg-secondary-500 text-background-50'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Video Grid */}
          {sortedVideos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {sortedVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/article/${video.id}`}
                  className="group rounded-xl overflow-hidden bg-background-100 hover:scale-[1.02] transition-all"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className={`px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${
                        video.isVip
                          ? 'bg-accent-500 text-background-50'
                          : 'bg-secondary-500 text-background-50'
                      }`}>
                        {video.isVip ? t('category.vip') : t('category.free')}
                      </span>
                    </div>
                    <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded bg-black/70 text-background-50 text-xs">
                      {video.duration}
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-50/90 text-primary-500 opacity-0 group-hover:opacity-100 transition-all transform group-hover:scale-100 scale-75">
                        <i className="ri-play-fill text-2xl"></i>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-foreground-950 mb-2 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {video.title}
                    </h3>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-accent-500 text-xs font-medium">
                          <i className="ri-star-fill mr-0.5"></i>{video.rating}
                        </span>
                        <span className="text-xs text-foreground-400">{video.views}次观看</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                <i className="ri-film-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-sm text-foreground-500">{t('category.noResults')}</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}